import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';
import { authColors, shadows } from '../../styles/colors';
import PublicHeader from '../../components/layout/PublicHeader';
import PublicFooter from '../../components/layout/PublicFooter';
import { plansService, Plan } from '../../services/supabase';
import { startCheckoutRedirect, STRIPE_PRICE_IDS } from '../../services/stripe';
import { useToast } from '../../context/ToastContext';
import SEO from '../../components/seo/SEO';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { clearCheckoutState, isReturningFromStripe, setPendingCheckout } from '../../utils/checkoutStorage';
import CheckoutLoadingScreen from '../../components/auth/CheckoutLoadingScreen';
import { trackUmamiEvent } from '../../utils/umami';
import { trackMetaInitiateCheckout } from '../../utils/metaPixel';
import { trackOpenAIInitiateCheckout } from '../../utils/openaiPixel';
import { formatStorageFromBytes, storageMbToBytes } from '../../utils/storageQuota';

/** Boolean plan features: tick (green) if true, cross (red) if false. Driven by DB. */
const BOOLEAN_PLAN_FEATURES: { key: 'has_ai_rewrite'; labelKey: string }[] = [
  { key: 'has_ai_rewrite', labelKey: 'pricing.featureAIRewrite' },
];

const FEATURE_SKELETON_COUNT = 5 + BOOLEAN_PLAN_FEATURES.length; // limit rows + boolean features

const FREE_CTA_CLASS = `w-full flex items-center justify-center ${authColors.primaryGradient} text-white py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all transform hover:scale-105 min-h-[48px] sm:min-h-[72px] md:py-3.5`;

const MOBILE_FEATURES_LIST = 'mt-5 space-y-3';

const PRICING_CARD_SHELL = `rounded-2xl ${shadows.medium} flex flex-col transition-all duration-300`;
const PRICING_CARD_FREE = `bg-white border border-gray-200 shadow-sm md:shadow-md ${PRICING_CARD_SHELL}`;
const PRICING_CARD_PRO_MONTHLY = `bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-500 ${PRICING_CARD_SHELL} relative`;
const PRICING_CARD_PRO_YEARLY = `bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-500 ${PRICING_CARD_SHELL} relative`;

const PricingPage: React.FC = () => {
  const { t, tSeo, language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const [freePlan, setFreePlan] = useState<Plan | null>(null);
  const [proPlan, setProPlan] = useState<Plan | null>(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [proBilling, setProBilling] = useState<'monthly' | 'yearly'>('monthly');

  const [loadError, setLoadError] = useState<string | null>(null);
  const autoCheckoutStartedRef = useRef(false);
  const cancelledToastShownRef = useRef(false);

  const checkoutParam = useMemo(() => {
    const v = searchParams.get('checkout');
    if (v === 'monthly' || v === 'yearly' || v === 'cancelled' || v === 'error') return v;
    return null;
  }, [searchParams]);

  // Note: no special logic on browser back from Stripe.
  // Stripe cancel_url returns to /pricing?checkout=cancelled (toast handled below).
  // For browser "back" returns, we detect a recent Stripe redirect flag and normalize to ?checkout=cancelled.
  useEffect(() => {
    if (checkoutParam === 'cancelled') return;
    if (checkoutParam === 'error') return;
    if (checkoutParam === 'monthly' || checkoutParam === 'yearly') return;

    if (isReturningFromStripe()) {
      clearCheckoutState();
      navigate('/pricing?checkout=cancelled', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutParam]);

  useEffect(() => {
    let cancelled = false;

    const safeSetState = (updater: () => void) => {
      if (!cancelled) {
        updater();
      }
    };

    const loadPlans = async () => {
      safeSetState(() => setPlansLoading(true));
      try {
        // Don't block the whole page on DB. If it takes too long, keep the skeleton.
        const plansData = await Promise.race([
          plansService.getAll(),
          new Promise<Plan[]>((resolve) => setTimeout(() => resolve([]), 2000)),
        ]);
        const free = plansData.find(plan => plan.name === 'free');
        const pro = plansData.find(plan => plan.name === 'pro');
        safeSetState(() => {
          setFreePlan(free || null);
          setProPlan(pro || null);
          setLoadError(null);
        });
      } catch (error) {
        console.error('Error loading plans:', error);
        safeSetState(() => setLoadError('pricing.loadError'));
      } finally {
        safeSetState(() => setPlansLoading(false));
      }
    };
    loadPlans();

    return () => {
      cancelled = true;
    };
  }, []);

  // Standard SaaS: Clean up any pending checkout if user cancelled, error, or manually navigated to pricing
  useEffect(() => {
    if (checkoutParam === 'cancelled' || checkoutParam === 'error' || !checkoutParam) {
      clearCheckoutState();
    }
  }, [checkoutParam]);

  // Show toast when payment is cancelled (only once per cancelled checkout)
  useEffect(() => {
    if (checkoutParam === 'cancelled' && !cancelledToastShownRef.current) {
      cancelledToastShownRef.current = true;
      showToast(t('auth.payment.cancelledMessage'), 'info');
    }
    // Reset when checkoutParam changes to something else
    if (checkoutParam !== 'cancelled') {
      cancelledToastShownRef.current = false;
    }
  }, [checkoutParam, showToast, t]);

  // Show toast when checkout error occurs
  useEffect(() => {
    if (checkoutParam === 'error') {
      showToast(t('pricing.checkoutError'), 'error');
    }
  }, [checkoutParam, showToast, t]);

  // Legacy support: if user comes from old redirect flow with ?checkout= param
  useEffect(() => {
    if (!user) return;
    if (!checkoutParam) return;
    if (checkoutParam === 'cancelled') return;
    if (checkoutParam === 'error') return;
    if (autoCheckoutStartedRef.current) return;
    if (isRedirecting) return;

    // TypeScript: checkoutParam is now guaranteed to be 'monthly' | 'yearly' here
    if (checkoutParam !== 'monthly' && checkoutParam !== 'yearly') return;

    autoCheckoutStartedRef.current = true;
    const priceId = checkoutParam === 'monthly' ? STRIPE_PRICE_IDS.monthly : STRIPE_PRICE_IDS.yearly;
    void handleCheckout(priceId, checkoutParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, checkoutParam, isRedirecting]);


  // Check if Stripe is configured
  const isStripeConfigured = STRIPE_PRICE_IDS.monthly && STRIPE_PRICE_IDS.yearly;

  // Intersection observers for scroll animations
  const heroRef = useIntersectionObserver({ threshold: 0.1, rootMargin: '0px 0px -100px 0px' });
  const cardsRef = useIntersectionObserver({ threshold: 0.1, rootMargin: '0px 0px -100px 0px' });

  const handleCheckout = async (priceId: string, type: 'monthly' | 'yearly') => {
    if (!priceId) {
      // Log for developers, but don't show technical error to users
      console.error('Stripe is not configured. Please set REACT_APP_STRIPE_PRICE_MONTHLY and REACT_APP_STRIPE_PRICE_YEARLY environment variables.');
      return;
    }

    // Standard SaaS : l'utilisateur doit être connecté pour s'abonner
    if (!user) {
      // Store checkout intent for direct checkout after auth
      setPendingCheckout(priceId, type);
      trackUmamiEvent('signup_click', { source: 'pricing_checkout' });
      navigate('/signup');
      return;
    }

    // Standard SaaS: show fullscreen "redirecting to payment" spinner
    setIsRedirecting(true);

    try {
      trackMetaInitiateCheckout(type);
      trackOpenAIInitiateCheckout(type);
      await startCheckoutRedirect(priceId, type);
    } catch (error) {
      setIsRedirecting(false);
      // Log error for developers, but don't show technical details to users
      console.error('Error during checkout:', error);
      // Show generic error message to user
      showToast(t('pricing.checkoutError'), 'error');
    }
  };

  const handleFreeCtaClick = () => {
    if (!user) trackUmamiEvent('signup_click', { source: 'pricing_free' });
  };

  const renderFeatures = (plan: Plan | null, listClassName = 'mt-8 space-y-4', compact = false) => {
    const iconClass = compact
      ? 'w-[18px] h-[18px] mr-2.5 mt-0.5 flex-shrink-0'
      : 'w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-0.5 flex-shrink-0';
    const CheckIcon = () => (
      <svg className={`${iconClass} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
    const CrossIcon = () => (
      <svg className={`${iconClass} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );

    if (!plan) {
      return (
        <ul className={listClassName}>
          {Array.from({ length: FEATURE_SKELETON_COUNT }).map((_, idx) => (
            <li key={idx} className="flex items-start">
              <div className={`${compact ? 'w-4 h-4 mr-2' : 'w-5 h-5 sm:w-6 sm:h-6 mr-3'} mt-0.5 rounded bg-gray-200 animate-pulse flex-shrink-0`} />
              <div className={`${compact ? 'h-3' : 'h-4'} w-3/4 rounded bg-gray-200 animate-pulse`} />
            </li>
          ))}
        </ul>
      );
    }

    const textClass = compact
      ? 'text-sm leading-relaxed'
      : 'text-sm sm:text-base';

    const limitLabels: string[] = [
      plan.max_leads === null
        ? t('pricing.featureUnlimitedLeads')
        : t('pricing.featureActiveLeads', { count: plan.max_leads }),
      plan.max_projects === null
        ? t('pricing.featureUnlimitedProjects')
        : t('pricing.featureActiveProjects', { count: plan.max_projects }),
      plan.max_payments === null
        ? t('pricing.featureUnlimitedPayments')
        : t('pricing.featureActivePayments', { count: plan.max_payments }),
      plan.max_message_templates === null
        ? t('pricing.featureUnlimitedTemplates')
        : t('pricing.featureLimitedTemplates', { count: plan.max_message_templates }),
      t('pricing.featureStorage', {
        size: formatStorageFromBytes(storageMbToBytes(plan.storage_mb), language),
      }),
    ];

    return (
      <ul className={listClassName}>
        {limitLabels.map((label, idx) => (
          <li key={idx} className={`flex items-start text-gray-700 ${textClass}`}>
            <CheckIcon />
            <span>{label}</span>
          </li>
        ))}
        {BOOLEAN_PLAN_FEATURES.map(({ key, labelKey }) => (
          <li key={key} className={`flex items-start text-gray-700 ${textClass}`}>
            {plan[key] ? <CheckIcon /> : <CrossIcon />}
            <span>{t(labelKey)}</span>
          </li>
        ))}
      </ul>
    );
  };

  if (user && isRedirecting) {
    return <CheckoutLoadingScreen messageKey="auth.redirectingToPayment" />;
  }

  const freeCtaLabel = user ? t('nav.dashboard') : t('pricing.ctaFree');
  const freeCtaTo = user ? '/app' : '/signup';
  const proPrice = proBilling === 'monthly' ? '18€' : '15€';
  const proThenLabel = proBilling === 'monthly'
    ? `18€${t('pricing.pricePerMonthTTC')}`
    : `180€${t('pricing.pricePerYearTTC')}`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={`${tSeo('pricing.title')} - Leadsme`}
        description={tSeo('pricing.description').replace(/\n/g, ' ')}
        canonical="https://leadsme.app/pricing"
      />
      <PublicHeader />

      {/* Pricing Section */}
      <section className="flex-1 relative bg-gradient-to-br from-blue-50 via-white to-cyan-50 pt-16 md:overflow-hidden md:flex md:items-center md:min-h-[calc(100vh-4rem)] lg:pt-0">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-0 pointer-events-none"></div>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-4 sm:py-12 md:py-20 md:flex md:flex-1 md:flex-col">
          <h1 className="sr-only sm:hidden">{t('pricing.title')}</h1>
          <div 
            ref={heroRef.elementRef}
            className={`hidden sm:block text-center mb-16 transition-all duration-700 ${heroRef.hasBeenVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <h1 className="text-5xl font-bold text-gray-900 sm:text-6xl">
              {t('pricing.title')}
            </h1>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto whitespace-pre-line">
              {t('pricing.description')}
            </p>
          </div>


          {/* Pricing Cards */}
          {loadError && (
            <div className="text-center pb-6">
              <p className="text-gray-700 mb-3">{t(loadError)}</p>
              <button
                onClick={async () => {
                  setLoadError(null);
                  setPlansLoading(true);
                  try {
                    const plansData = await plansService.getAll();
                    const free = plansData.find(plan => plan.name === 'free');
                    const pro = plansData.find(plan => plan.name === 'pro');
                    setFreePlan(free || null);
                    setProPlan(pro || null);
                  } catch (error) {
                    console.error('Error loading plans:', error);
                    setLoadError('pricing.loadError');
                  } finally {
                    setPlansLoading(false);
                  }
                }}
                disabled={plansLoading}
                className={`${authColors.primaryGradient} text-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60`}
              >
                {plansLoading ? t('common.loading') : t('common.retry')}
              </button>
            </div>
          )}

          <div 
            ref={cardsRef.elementRef}
            className={`max-w-md md:max-w-6xl mx-auto w-full transition-all duration-700 md:flex-1 md:flex md:flex-col md:min-h-0 ${cardsRef.hasBeenVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="flex flex-col gap-6 md:grid md:grid-cols-3 md:gap-8">
              {/* Carte Gratuit */}
              <div className={`${PRICING_CARD_FREE} flex flex-col p-5 sm:p-8 md:p-8 md:hover:shadow-2xl md:transform md:hover:-translate-y-1`}>
                <div>
                  <div className="flex items-start justify-between gap-4 md:block">
                    <div className="min-w-0">
                      <h2 className="text-xl sm:text-2xl md:text-2xl font-bold text-gray-900">
                        {t('pricing.planFree')}
                      </h2>
                      <p className="mt-1 sm:mt-2 md:mt-2 text-sm sm:text-base text-gray-600">
                        {t('pricing.planFreeDescription')}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0 md:mt-6 md:text-left">
                      <div className="flex items-baseline gap-1 sm:gap-2 justify-end md:justify-start">
                        <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">0€</span>
                        <span className="text-sm sm:text-base md:text-gray-600">{t('pricing.pricePerMonth')}</span>
                      </div>
                      <p className="hidden md:block text-sm text-transparent mt-2 h-5">
                        {/* Espace réservé pour aligner avec les autres cartes */}
                      </p>
                    </div>
                  </div>

                  <div className="hidden md:block">
                    {renderFeatures(freePlan)}
                  </div>
                </div>

                <Link
                  to={freeCtaTo}
                  onClick={handleFreeCtaClick}
                  className={`hidden md:flex ${FREE_CTA_CLASS} mt-8`}
                >
                  {freeCtaLabel}
                </Link>

                {/* Mobile — CTA avant features (conversion) */}
                <div className="mt-5 sm:mt-6 md:hidden">
                  <Link
                    to={freeCtaTo}
                    onClick={handleFreeCtaClick}
                    className={FREE_CTA_CLASS}
                  >
                    {freeCtaLabel}
                  </Link>
                </div>
                <div className="md:hidden">
                  {renderFeatures(freePlan, MOBILE_FEATURES_LIST, true)}
                </div>
              </div>

              {/* Carte Pro Mensuel — desktop */}
              <div className={`hidden md:flex ${PRICING_CARD_PRO_MONTHLY} p-8 hover:shadow-2xl transition-all transform hover:-translate-y-1`}>
                <div className="absolute -top-4 right-6">
                  <span className={`${authColors.primaryGradient} text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg`}>
                    {t('pricing.planPopular')}
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {t('pricing.planPro')}
                  </h3>
                  <p className="mt-2 text-gray-600">
                    {t('pricing.planProDescription')}
                  </p>
                  <div className="mt-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-gray-900">18€</span>
                      <span className="text-gray-600">{t('pricing.pricePerMonthTTC')}</span>
                    </div>
                    <div className="mt-2 h-5" aria-hidden />
                  </div>
                  {renderFeatures(proPlan)}
                </div>
                <button
                  onClick={() => handleCheckout(STRIPE_PRICE_IDS.monthly, 'monthly')}
                  disabled={isRedirecting || !isStripeConfigured}
                  className={`mt-8 w-full flex flex-col items-center justify-center ${authColors.primaryGradient} text-white py-3.5 px-4 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed min-h-[72px]`}
                >
                  <span>{t('pricing.tryFreeFor14Days')}</span>
                  <span className="text-xs font-normal opacity-90 mt-1">{`${t('pricing.then')} 18€${t('pricing.pricePerMonthTTC')}`}</span>
                </button>
              </div>

              {/* Carte Pro Annuel — desktop */}
              <div className={`hidden md:flex ${PRICING_CARD_PRO_YEARLY} p-8 hover:shadow-2xl transition-all transform hover:-translate-y-1`}>
                <div className="absolute -top-4 right-6">
                  <span className="bg-green-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                    {t('pricing.bestPrice')}
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {t('pricing.planPro')} {t('pricing.annual')}
                  </h3>
                  <p className="mt-2 text-gray-600">
                    {t('pricing.planProDescriptionYearly')}
                  </p>
                  <div className="mt-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-gray-900">15€</span>
                      <span className="text-gray-600">{t('pricing.pricePerMonthTTC')}</span>
                    </div>
                    <p className="text-sm text-transparent mt-2 h-5">
                      {/* Espace réservé pour aligner avec les autres cartes */}
                    </p>
                  </div>
                  {renderFeatures(proPlan)}
                </div>
                <button
                  onClick={() => handleCheckout(STRIPE_PRICE_IDS.yearly, 'yearly')}
                  disabled={isRedirecting || !isStripeConfigured}
                  className="mt-8 w-full flex flex-col items-center justify-center bg-green-600 hover:bg-green-700 text-white py-3.5 px-4 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed min-h-[72px]"
                >
                  <span>{t('pricing.tryFreeFor14Days')}</span>
                  <span className="text-xs font-normal opacity-90 mt-1">{`${t('pricing.then')} 180€${t('pricing.pricePerYearTTC')}`}</span>
                </button>
              </div>

              {/* Pro compact — mobile (même structure que Gratuit) */}
              <div className="md:hidden">
                <div
                  className={`flex flex-col ${
                    proBilling === 'monthly' ? PRICING_CARD_PRO_MONTHLY : PRICING_CARD_PRO_YEARLY
                  } p-5`}
                >
                  <div className="absolute -top-3 right-4">
                    <span
                      className={`text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg transition-colors duration-300 ${
                        proBilling === 'monthly' ? authColors.primaryGradient : 'bg-green-600'
                      }`}
                    >
                      {proBilling === 'monthly' ? t('pricing.planPopular') : t('pricing.bestPrice')}
                    </span>
                  </div>

                  <div className="flex rounded-full bg-white/70 backdrop-blur-sm p-1 mb-4 shadow-inner">
                    <button
                      type="button"
                      onClick={() => setProBilling('monthly')}
                      className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                        proBilling === 'monthly'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-gray-500'
                      }`}
                    >
                      {t('pricing.monthly')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setProBilling('yearly')}
                      className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                        proBilling === 'yearly'
                          ? 'bg-white text-green-700 shadow-sm'
                          : 'text-gray-500'
                      }`}
                    >
                      {t('pricing.annual')}
                    </button>
                  </div>

                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold text-gray-900">
                        {proBilling === 'yearly'
                          ? `${t('pricing.planPro')} ${t('pricing.annual')}`
                          : t('pricing.planPro')}
                      </h3>
                      <p className="mt-1.5 text-sm text-gray-600">
                        {proBilling === 'yearly'
                          ? t('pricing.planProDescriptionYearly')
                          : t('pricing.planProDescription')}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-baseline gap-1 justify-end">
                        <span className="text-3xl font-bold text-gray-900 tracking-tight">{proPrice}</span>
                        <span className="text-sm text-gray-500">{t('pricing.pricePerMonthTTC')}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleCheckout(
                      proBilling === 'monthly' ? STRIPE_PRICE_IDS.monthly : STRIPE_PRICE_IDS.yearly,
                      proBilling,
                    )}
                    disabled={isRedirecting || !isStripeConfigured}
                    className={`mt-5 w-full flex flex-col items-center justify-center text-white rounded-xl font-semibold shadow-md active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] py-2.5 ${
                      proBilling === 'monthly'
                        ? authColors.primaryGradient
                        : 'bg-green-600 active:bg-green-700'
                    }`}
                  >
                    <span className="text-sm font-semibold">{t('pricing.tryFreeFor14Days')}</span>
                    <span className="text-xs font-normal opacity-90">{`${t('pricing.then')} ${proThenLabel}`}</span>
                  </button>

                  {renderFeatures(proPlan, MOBILE_FEATURES_LIST, true)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PricingPage;
